import { ArrowSquareOut, LinkedinLogo } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import Checkbox from "common/components/ui/Checkbox";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { getProfileSubtitle } from "common/helpers/utils";
import type { LinkedInProfile } from "common/types";

import type { ReactNode } from "react";

type ProfileCardProps = {
  isLoading: boolean;
  profile?: LinkedInProfile;
  openPreview?: (profileId: string) => void;
  children?: ReactNode;
} & (
  | { isSelected?: boolean; onSelect?: () => void }
  | { isSelected: never; onSelect: never }
);

export default function ProfileCard({
  isLoading,
  profile,
  openPreview,
  isSelected,
  onSelect,
  children,
}: ProfileCardProps) {
  const workspace = useSelectedWorkspaceContext();

  const {
    id,
    picture_url: pictureUrl,
    full_name: fullName,
    premium,
    public_identifier: publicIdentifier,
  } = profile ?? {};

  //this is false if the user doesn't have permissions to open lead preview
  const canInteractWithCard =
    !openPreview || workspace.permissions.includes("leads_full");

  const containerClassName = clsx(
    "group flex flex-1 h-16 items-center bg-white rounded-3xl border px-4 transition-[background,border] gap-x-2",
    isSelected ? "border-purple-500" : "border-black-200",
    !isLoading && canInteractWithCard && "hover:bg-black-100",
  );
  const content = (
    <>
      {onSelect && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      )}
      {isLoading ? (
        <Skeleton className="size-10 rounded-full" />
      ) : (
        <ProfileImage src={pictureUrl} />
      )}

      <div className="flex flex-1 flex-col gap-y-0.5">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-4 w-44" />
          </>
        ) : (
          <>
            <div className="flex gap-x-1 items-center">
              <span className="line-clamp-1 break-all text-sm-bold">
                {fullName}
              </span>
              {premium && (
                <LinkedinLogo
                  className="text-yellow-500 size-4"
                  weight="fill"
                />
              )}
            </div>
            <p className="line-clamp-1 break-all text-xs-medium text-black-500 text-left">
              {getProfileSubtitle(profile)}
            </p>
          </>
        )}
      </div>
      {children}
      {!openPreview && !isLoading && (
        <ArrowSquareOut size={20} className="invisible group-hover:visible" />
      )}
    </>
  );

  if (isLoading || !canInteractWithCard) {
    return <div className={containerClassName}>{content}</div>;
  }
  if (!openPreview) {
    return (
      <Link
        className={containerClassName}
        to={`https://linkedin.com/in/${publicIdentifier}`}
        target="_blank"
        rel="noreferrer"
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={containerClassName}
      onClick={() => openPreview(id)}
    >
      {content}
    </button>
  );
}
